import { useEffect, useRef, useState } from "react";
import type { Idea, Message, Project, Repo } from "../lib/types";
import type { DiveConnection } from "../lib/dive";
import { sendToDive, queueDiveTask } from "../lib/dive";
import { renderMarkdown } from "../lib/markdown";
import { StatusBadge } from "./StatusBadge";
import { IssueModal } from "./IssueModal";
import { toast } from "./Toast";

interface Props {
  idea: Idea;
  messages: Message[];
  projects: Project[];
  repos: Repo[];
  diveConnection: DiveConnection | null;
  onUpdateIdea: (updates: Partial<Idea>) => void;
  onAddMessage: (msg: Omit<Message, "id" | "updatedAt" | "createdAt" | "deletedAt">) => void;
}

export function RefineView({ idea, messages, projects, repos, diveConnection, onUpdateIdea, onAddMessage }: Props) {
  const [userInput, setUserInput] = useState("");
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [sendingToDive, setSendingToDive] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [brainDumpExpanded, setBrainDumpExpanded] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const startEditTitle = () => {
    setTitleDraft(idea.title);
    setEditingTitle(true);
    setTimeout(() => { titleInputRef.current?.select(); }, 0);
  };

  const commitTitle = () => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== idea.title) onUpdateIdea({ title: trimmed });
    setEditingTitle(false);
  };

  const brainDumpLong = idea.brainDump.length > 160 || idea.brainDump.split("\n").length > 3;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const resizeTextarea = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  };

  const wrapSelection = (prefix: string, suffix: string = prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = userInput.slice(start, end);
    const newValue = userInput.slice(0, start) + prefix + selected + suffix + userInput.slice(end);
    setUserInput(newValue);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, end + prefix.length);
      resizeTextarea();
    }, 0);
  };

  const insertCodeBlock = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const selected = userInput.slice(start, ta.selectionEnd);
    const block = `\`\`\`\n${selected || "code here"}\n\`\`\``;
    const newValue = userInput.slice(0, start) + block + userInput.slice(ta.selectionEnd);
    setUserInput(newValue);
    setTimeout(() => {
      ta.focus();
      resizeTextarea();
    }, 0);
  };

  const submitMessage = () => {
    if (!userInput.trim()) return;
    onAddMessage({
      userId: idea.userId,
      ideaId: idea.id,
      role: "user",
      content: userInput.trim(),
      hidden: false,
    });
    setUserInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    if (!navigator.onLine) {
      toast({ kind: "info", message: "Saved locally — AI will respond when you're back online" });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submitMessage();
    }
  };

  const handleFiled = (_issueUrl: string) => {
    onUpdateIdea({ status: "filed", updatedAt: Date.now() });
  };

  const handleSendToDive = async () => {
    if (sendingToDive) return;
    if (!diveConnection) {
      toast({ kind: "error", message: "Connect Dive in Settings first" });
      return;
    }
    setSendingToDive(true);
    try {
      if (!navigator.onLine) {
        queueDiveTask(idea.title);
        toast({ kind: "info", message: "Offline — queued, will send when reconnected" });
        return;
      }
      await sendToDive(diveConnection, idea.title);
      toast({ kind: "success", message: "Task added to Dive" });
    } catch {
      queueDiveTask(idea.title);
      toast({ kind: "info", message: "Send failed — queued for retry when reconnected" });
    } finally {
      setSendingToDive(false);
    }
  };

  const visibleMessages = messages.filter((m) => !m.hidden);

  return (
    <div className="flex flex-col h-full">
      {/* Idea header */}
      <div className="px-4 py-4 border-b border-gray-100 shrink-0">
        <div className="flex items-start gap-2 mb-2">
          <StatusBadge status={idea.status} />
          <select
            value={idea.projectId ?? ""}
            onChange={(e) =>
              onUpdateIdea({ projectId: e.target.value || null, updatedAt: Date.now() })
            }
            className="text-xs text-gray-400 font-mono bg-transparent focus:outline-none"
          >
            <option value="">Inbox</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {editingTitle ? (
          <input
            ref={titleInputRef}
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitTitle(); }
              if (e.key === "Escape") setEditingTitle(false);
            }}
            className="font-syne text-xl font-bold text-gray-900 leading-tight w-full bg-transparent border-b-2 border-gray-800 focus:outline-none pb-0.5"
          />
        ) : (
          <h2
            className="font-syne text-xl font-bold text-gray-900 leading-tight cursor-text hover:text-gray-600 transition-colors"
            onClick={startEditTitle}
            title="Click to edit"
          >
            {idea.title}
          </h2>
        )}

        {idea.brainDump && (
          <div className="mt-2">
            <p className={`text-xs text-gray-500 font-mono whitespace-pre-wrap ${brainDumpExpanded ? "" : "line-clamp-3"}`}>
              {idea.brainDump}
            </p>
            {brainDumpLong && (
              <button
                onClick={() => setBrainDumpExpanded(!brainDumpExpanded)}
                className="text-xs text-gray-400 hover:text-gray-700 font-mono mt-0.5 transition-colors"
              >
                {brainDumpExpanded ? "Show less ↑" : "Show more ↓"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Message thread */}
      <div className="flex-1 overflow-y-auto px-4 py-4 pb-2">
        {visibleMessages.length === 0 && (
          <div className="text-center py-10 text-gray-400 text-xs font-mono px-4">
            <p>No messages yet.</p>
            <p className="mt-3 text-gray-300 leading-relaxed">
              Ask Claude (in Claude Code or claude.ai) to refine this idea — its questions appear here.
            </p>
          </div>
        )}
        <div className="flex flex-col gap-3">
          {visibleMessages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}
        </div>
        <div ref={bottomRef} />
      </div>

      {/* User input */}
      <div className="px-4 pb-4 shrink-0">
        <div className="border border-gray-200 rounded-lg focus-within:border-gray-800 transition-colors">
          {/* Toolbar */}
          <div className="flex items-center gap-0.5 px-2 pt-1.5 pb-1 border-b border-gray-100">
            {[
              { label: "B", title: "Bold", action: () => wrapSelection("**"), style: "font-bold" },
              { label: "I", title: "Italic", action: () => wrapSelection("*"), style: "italic" },
              { label: "`", title: "Inline code", action: () => wrapSelection("`"), style: "" },
              { label: "</>", title: "Code block", action: insertCodeBlock, style: "" },
              { label: "•", title: "Bullet list", action: () => wrapSelection("- ", ""), style: "text-base leading-none" },
            ].map(({ label, title, action, style }) => (
              <button
                key={label}
                type="button"
                title={title}
                onMouseDown={(e) => { e.preventDefault(); action(); }}
                className={`px-2 py-0.5 rounded text-xs font-mono text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors ${style}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-end px-1 pb-1 pt-1">
            <textarea
              ref={textareaRef}
              value={userInput}
              onChange={(e) => { setUserInput(e.target.value); resizeTextarea(); }}
              onKeyDown={handleKeyDown}
              placeholder="Add notes or questions… (⌘↵ to send)"
              rows={1}
              className="flex-1 px-2 py-1.5 text-sm font-mono focus:outline-none resize-none overflow-hidden leading-relaxed bg-transparent"
              style={{ minHeight: "38px" }}
            />
            <button
              onClick={submitMessage}
              disabled={!userInput.trim()}
              className="bg-gray-900 text-white rounded-md px-3 text-sm font-mono font-bold disabled:opacity-40 hover:bg-gray-700 transition-colors shrink-0 mb-1"
              style={{ height: "34px" }}
            >
              ↵
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center mt-3">
          <span className="text-xs text-gray-300 font-mono">
            {idea.status !== "filed" ? "→ GH Issue when ready" : ""}
          </span>
          <div className="flex items-center gap-2">
            {diveConnection && (
              <button
                onClick={handleSendToDive}
                disabled={sendingToDive}
                className="text-xs font-mono text-blue-500 hover:text-blue-700 border border-blue-200 rounded px-2.5 py-1.5 transition-colors disabled:opacity-40"
              >
                {sendingToDive ? "Sending…" : "→ Dive"}
              </button>
            )}
            {idea.status !== "filed" && (
              <button
                onClick={() => setShowIssueModal(true)}
                className="text-xs font-mono text-gray-500 hover:text-gray-900 border border-gray-200 rounded px-2.5 py-1.5 transition-colors"
              >
                → GH Issue
              </button>
            )}
          </div>
          {idea.status === "filed" && idea.preferredRepo && (
            <span className="text-xs text-purple-500 font-mono">Filed in {idea.preferredRepo}</span>
          )}
        </div>
      </div>

      {showIssueModal && (
        <IssueModal
          idea={idea}
          messages={messages}
          projects={projects}
          repos={repos}
          onClose={() => setShowIssueModal(false)}
          onFiled={handleFiled}
        />
      )}
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === "user";
  const html = renderMarkdown(message.content);
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
          isUser
            ? "bg-gray-900 text-white rounded-br-sm"
            : "bg-gray-100 text-gray-800 rounded-bl-sm"
        }`}
      >
        <div
          className="md-content"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}
